import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { FREE_DOMAIN_CONFIGS } from 'helpers/helpersSuccessDomain';
import { getOnlyTLD } from 'helpers/helper';
import postKTP, {
  fetchCheckOnlineId,
  fetchCheckOnlineIdExist,
  fetchMyVoucher,
  fetchSendDomainTerm,
  fetchSendNpwp,
  fetchSendSuratSubdomain,
  fetchSendPph,
} from "./cartAPI";

// Helper function to calculate total price of a product including addons
const calculateProductTotalPrice = (product) => {
  // Always calculate the total price including addons, regardless of priceWithAddon
  let totalPrice = Number(product.price || 0);
  
  // Add setup fee if available
  if (product.setupfee !== undefined) {
    const setupFee = Number(product.setupfee || 0);
    totalPrice += (setupFee === -1 ? 0 : setupFee);
  }
  
  // Add VPS addons price
  if (product.vpsaddons?.length > 0) {
    const { vpsaddons, billingcycle } = product;
    
    const addonsPrice = vpsaddons.reduce((total, addon) => {
      // Handle different billing cycle formats
      let addonPrice = 0;
      let addonSetupFee = 0;
      
      // Check if addon has billingcycle property that indicates it's free
      if (addon.billingcycle === "free") {
        addonPrice = 0;
        addonSetupFee = 0;
      } else {
        // Get price from billing cycle specific property (e.g., annually, monthly)
        if (addon[billingcycle] !== undefined) {
          addonPrice = +addon[billingcycle];
        } else if (addon.price !== undefined) {
          // Fallback to general price property
          addonPrice = +addon.price;
        }
        
        // Get setup fee
        if (addon.setupfee !== undefined) {
          addonSetupFee = +addon.setupfee;
        } else if (addon[billingcycle + 'setupfee'] !== undefined) {
          addonSetupFee = +addon[billingcycle + 'setupfee'];
        }
      }
      
      // Handle special values that indicate free
      if (addonPrice === -1 || addonPrice === -1.00 || addonPrice === 0 || addonPrice === 0.00) {
        addonPrice = 0;
      }
      if (addonSetupFee === -1 || addonSetupFee === -1.00 || addonSetupFee === 0 || addonSetupFee === 0.00) {
        addonSetupFee = 0;
      }
      
      return total + addonPrice + addonSetupFee;
    }, 0);
    
    totalPrice += addonsPrice;
  }
  
  // Add config options price
  if (product.configoptions?.length > 0) {
    const configPrice = product.configoptions.reduce((total, config) => {
      const configPrice = +config.price || 0;
      return total + (configPrice === -1 ? 0 : configPrice);
    }, 0);
    totalPrice += configPrice;
  }
  
  // console.log('🛒 Product total calculation:', {
  //   productName: product.name,
  //   basePrice: product.price,
  //   setupFee: product.setupfee,
  //   addonsCount: product.vpsaddons?.length || 0,
  //   addonsPrice: product.vpsaddons?.reduce((total, addon) => {
  //     const price = +addon.price || 0;
  //     const setupFee = +addon.setupfee || 0;
  //     return total + (price === -1 ? 0 : price) + (setupFee === -1 ? 0 : setupFee);
  //   }, 0) || 0,
  //   totalPrice
  // });
  
  return totalPrice;
};

// Helper function to recalculate cart totals
const recalculateCartTotals = (state) => {
  // console.log('Recalculating cart totals...');
  // console.log('Current productsList:', state.productsList);
  
  let newSubtotal = 0;
  state.productsList.forEach(product => {
    const productTotal = calculateProductTotalPrice(product);
    // console.log(`Product ${product.pid} total:`, productTotal);
    newSubtotal += productTotal;
  });
  
  // console.log('New subtotal:', newSubtotal);
  state.subtotal = newSubtotal;
  state.tax.price = state.subtotal * state.tax.percentage;
  state.total = state.subtotal + state.tax.price;
  
  // console.log('Final cart totals - Subtotal:', state.subtotal, 'Tax:', state.tax.price, 'Total:', state.total);
};

export const sendDocumentNpwp = createAsyncThunk(
  "cart/sendNPWP",
  async (documentNpwp) => {
    const response = await fetchSendNpwp(documentNpwp);
    return response;
  }
);

export const sendDocumentPph = createAsyncThunk(
  "cart/sendPph",
  async (documentPph) => {
    const response = await fetchSendPph(documentPph);
    return response;
  }
);

export const sendDocumentSubdomain = createAsyncThunk(
  "cart/sendSuratSubdomain",
  async (documentSubdomain) => {
    const response = await fetchSendSuratSubdomain(documentSubdomain);
    return response;
  }
);

export const sendDocumentDomainTerm = createAsyncThunk(
  "cart/sendDomainTerm",
  async (documentDomainTerm) => {
    const response = await fetchSendDomainTerm(documentDomainTerm);
    return response;
  }
);

export const sendCheckOnlineId = createAsyncThunk(
  "cart/sendCheckOnlineId",
  async (claim) => {
    const response = await fetchCheckOnlineId(claim);
    return response;
  }
);

export const sendCheckOnlineIdExist = createAsyncThunk(
  "cart/sendCheckOnlineIdExist",
  async (email) => {
    const response = await fetchCheckOnlineIdExist(email);
    return response;
  }
);

export const getMyVoucher = createAsyncThunk("cart/getVoucher", async () => {
  const response = await fetchMyVoucher();
  return response;
});

export const uploadKTP = createAsyncThunk(
  "cart/uploadKTP",
  async (image, { rejectWithValue }) => {
    try {
      const response = await postKTP(image);
      const data = await response.json();
      if (data.code !== 200) {
        throw new Error(data.message);
      }
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  orderProcessName: null,
  productsList: [],
  paymentMethod: null,
  orderProcess: false,
  duplicateStatus: false,
  tax: {
    percentage: 0.11,
    price: 0,
  },
  requirement: {
    company: false,
    tos: false,
    pph: false,
  },
  promoCode: {
    queryCode: null,
    code: null,
    discountPrice: 0,
    myVoucher: null,
    description: null,
    pid: null,
    realprice: null,
    priceOverrideDomain: 0,
    priceOverrideHosting: 0,
    customPromo: "",
    paymentGateway: "",
  },
  ktpCard: {
    uploadStatus: "waiting",
    imgUrlResponse: "",
    code: 404,
    message: "",
  },
  conditionalDomain: {
    status: "idle",
    error: "",
    isSkipAll: false,
  },
  availableOnlineId: {
    status: "idle",
    error: "",
    data: "",
  },
  documentNpwp: {
    status: "idle",
    error: "",
  },
  documentPph: {
    status: "idle",
    error: "",
    url: "",
  },
  documentSuratSubdomain: {
    status: "idle",
    suratUrl: null,
    error: "",
  },
  additionalFee: 0,
  subtotal: 0,
  total: 0,
  pricepph: 0,
  domainBundle: {
    onlineId: null,
  },
  onlineIdCheckExist: {
    status: "idle",
    error: "",
    exist: false,
  },
  orderParams: {
    pid: null,
    billingcycle: null,
  },
  // velocityProduct: null,
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart: (state, action) => {
      const checkProduct = state.productsList.find(
        (list) => list.pid === action.payload.pid
      );

      // console.log('action.payload', action.payload);
      
      if (!checkProduct) {
        state.duplicateStatus = false;
        
        state.productsList.push(action.payload);
        
        // Recalculate cart totals using helper function
        recalculateCartTotals(state);

        // Jika produk yang ditambahkan adalah hosting dengan durasi minimal 1 tahun,
        // cek apakah ada domain di cart yang bisa menjadi gratis
        if (action.payload.type === "hostingaccount") {
          const validBillingCycles = ["annually", "biennially", "triennially"];
          if (validBillingCycles.includes(action.payload.billingcycle)) {
            const hostingPid = action.payload.pid;

            // Get allowed extensions based on hosting PID
            let allowedExtensions = [];
            
            if (FREE_DOMAIN_CONFIGS.myidBizidOnly.pids.includes(hostingPid)) {
              allowedExtensions = FREE_DOMAIN_CONFIGS.myidBizidOnly.extensions;
            } else if (FREE_DOMAIN_CONFIGS.comAndSomeId.pids.includes(hostingPid)) {
              allowedExtensions = FREE_DOMAIN_CONFIGS.comAndSomeId.extensions;
            } else if (FREE_DOMAIN_CONFIGS.someIdExtensions.pids.includes(hostingPid)) {
              allowedExtensions = FREE_DOMAIN_CONFIGS.someIdExtensions.extensions;
            } else if (FREE_DOMAIN_CONFIGS.comAndSomeId2.pids.includes(hostingPid)) {
              allowedExtensions = FREE_DOMAIN_CONFIGS.comAndSomeId2.extensions;
            } else if (FREE_DOMAIN_CONFIGS.myidOnly.pids.includes(hostingPid)) {
              allowedExtensions = FREE_DOMAIN_CONFIGS.myidOnly.extensions;
            } else if (FREE_DOMAIN_CONFIGS.allExtensions.pids.includes(hostingPid)) {
              allowedExtensions = FREE_DOMAIN_CONFIGS.allExtensions.extensions;
            } else if (FREE_DOMAIN_CONFIGS.premiumExtensions.pids.includes(hostingPid)) {
              allowedExtensions = FREE_DOMAIN_CONFIGS.premiumExtensions.extensions;
            }

            // Cari domain di cart yang bisa menjadi gratis
            if (allowedExtensions.length > 0) {
              // Cek apakah sudah ada domain gratis di cart
              const hasFreeDomainInCart = state.productsList.some(
                item => (item.type === "register" || item.type === "transfer") && 
                        item.price === 0 && 
                        item.duration === "1"
              );
              
              if (!hasFreeDomainInCart) {
                // Ambil domain yang pertama kali ditambahkan yang memenuhi syarat
                const eligibleDomainIndex = state.productsList.findIndex((item) => {
                  return (item.type === "register" || item.type === "transfer") &&
                         item.duration === "1" &&
                         item.price > 0 &&
                         allowedExtensions.includes(getOnlyTLD(item.domain));
                });

                if (eligibleDomainIndex !== -1) {
                  // Set domain menjadi gratis
                  state.productsList[eligibleDomainIndex].price = 0;
                  
                  // Recalculate cart totals
                  recalculateCartTotals(state);
                }
              }
            }
          }
        }
      } else {
        state.duplicateStatus = true;
      }
    },
    changeProductFromCart: (state, action) => {
      // For server products, use both gid and pid to find the exact product
      let index = -1;
      let checkProduct = null;
      
      if (action.payload.type === 'server' && action.payload.gid && action.payload.pid) {
        // For server products, find by gid and pid combination
        index = state.productsList.findIndex(
          (item) => item.gid === action.payload.gid && item.pid === action.payload.pid
        );
        checkProduct = state.productsList.find(
          (item) => item.gid === action.payload.gid && item.pid === action.payload.pid
        );
      } else {
        // For other products, use type as before
        index = state.productsList.findIndex(
          (item) => item.type === action.payload.type
        );
        checkProduct = state.productsList.find(
          (item) => item.type === action.payload.type
        );
      }

      if (checkProduct) {
        if (action.payload.priceWithAddon) {
          if (index !== -1) {
            // Calculate the old total price of the product including addons
            const oldProduct = state.productsList[index];
            const oldTotalPrice = calculateProductTotalPrice(oldProduct);
            
            // Subtract old price from subtotal
            state.subtotal -= oldTotalPrice;
            
            // Preserve existing addons and custom fields
            const existingProduct = state.productsList[index];
            const updatedProduct = {
              ...action.payload,
              // Preserve existing addons
              vpsaddons: existingProduct.vpsaddons || [],
              configoptions: existingProduct.configoptions || [],
              customfields: existingProduct.customfields || []
            };
            
            state.productsList[index] = updatedProduct;
          } else {
            state.productsList.push(action.payload);
          }

          // Recalculate cart totals using helper function
          recalculateCartTotals(state);
        } else {
          if (index !== -1) {
            // Calculate the old total price of the product including addons
            const oldProduct = state.productsList[index];
            const oldTotalPrice = calculateProductTotalPrice(oldProduct);
            
            // Subtract old price from subtotal
            state.subtotal -= oldTotalPrice;
            
            // Preserve existing addons and custom fields
            const existingProduct = state.productsList[index];
            const updatedProduct = {
              ...action.payload,
              // Preserve existing addons
              vpsaddons: existingProduct.vpsaddons || [],
              configoptions: existingProduct.configoptions || [],
              customfields: existingProduct.customfields || []
            };
            
            state.productsList[index] = updatedProduct;
          } else {
            state.productsList.push(action.payload);
          }

          // Recalculate cart totals using helper function
          recalculateCartTotals(state);
        }
      }
    },
    removeCartItem: (state, action) => {
      const currentList = state.productsList;
      
      // Find items to be deleted
      const getProductDeleted = currentList.find(list => list.pid === action.payload);
      const getDomainDeleted = currentList.find(list => list.domain === action.payload);
      const getDomainDeletedWhois = currentList.find(list => list.domainWhois === action.payload);
      const getWhoisDeleted = currentList.find(list => list.whois === action.payload);
      const getBackorderDeleted = currentList.find(list => list.type === "backorder");
      const getDomainBackorderDeleted = currentList.find(list => list.type === "backorder-register");

      // Early return if no item found
      if (!getProductDeleted && !getDomainDeleted && !getWhoisDeleted) {
        return;
      }

      if (getProductDeleted) {
        // Handle product deletion
        state.productsList = currentList.filter(list => list.pid !== action.payload);

        // Reset existing domain when product is removed
        state.existingDomain = null;
      } else if (getDomainDeleted) {
        // Handle domain deletion
        state.productsList = currentList.filter(list => 
          list.domain !== action.payload && 
          list.domainWhois !== action.payload && 
          list.premium_domain !== action.payload &&
          !(list.type === "whois" && list.domainWhois === action.payload)
        );

        // Reset existing domain when domain is removed
        if (getDomainDeleted.type === "domain-existing") {
          state.existingDomain = null;
        }

        // Reset domain bundle if needed
        if (getDomainDeleted.type === "register") {
          state.domainBundle = { onlineId: null };
        }
      } else if (getWhoisDeleted) {
        // Handle whois deletion
        state.productsList = currentList.filter(list => list.whois !== action.payload);
      }

      // Always recalculate cart totals using helper function to ensure accurate PPN calculation
      recalculateCartTotals(state);
      state.duplicateStatus = false;
    },
    addDomainToCart: (state, action) => {
      const checkDomain = state.productsList.find(
        (list) => list.domain === action.payload.domain
      );

      const checkDomainBackorder = state.productsList.find(
        (list) => list.type === action.payload.type
      );

      if (!checkDomain) {
        if (action.payload.type === "domain-existing") {
          state.existingDomain = action.payload;
        } else if (action.payload.type === "register") {
          // Cek apakah sudah ada hosting dengan durasi minimal 1 tahun di cart
          const hostingItem = state.productsList.find(item => item.type === "hostingaccount");
          const validBillingCycles = ["annually", "biennially", "triennially"];
          let shouldBeFree = false;

          if (hostingItem && validBillingCycles.includes(hostingItem.billingcycle)) {
            // Cek apakah sudah ada domain gratis lain di cart (register ATAU transfer)
            const hasFreeDomainInCart = state.productsList.some(
              item => (item.type === "register" || item.type === "transfer") && 
                      item.price === 0 && 
                      item.duration === "1"
            );

            if (!hasFreeDomainInCart && action.payload.duration === "1") {
              const hostingPid = hostingItem.pid;
              
              // Get allowed extensions based on hosting PID
              let allowedExtensions = [];
              
              if (FREE_DOMAIN_CONFIGS.myidBizidOnly.pids.includes(hostingPid)) {
                allowedExtensions = FREE_DOMAIN_CONFIGS.myidBizidOnly.extensions;
              } else if (FREE_DOMAIN_CONFIGS.comAndSomeId.pids.includes(hostingPid)) {
                allowedExtensions = FREE_DOMAIN_CONFIGS.comAndSomeId.extensions;
              } else if (FREE_DOMAIN_CONFIGS.someIdExtensions.pids.includes(hostingPid)) {
                allowedExtensions = FREE_DOMAIN_CONFIGS.someIdExtensions.extensions;
              } else if (FREE_DOMAIN_CONFIGS.comAndSomeId2.pids.includes(hostingPid)) {
                allowedExtensions = FREE_DOMAIN_CONFIGS.comAndSomeId2.extensions;
              } else if (FREE_DOMAIN_CONFIGS.myidOnly.pids.includes(hostingPid)) {
                allowedExtensions = FREE_DOMAIN_CONFIGS.myidOnly.extensions;
              } else if (FREE_DOMAIN_CONFIGS.allExtensions.pids.includes(hostingPid)) {
                allowedExtensions = FREE_DOMAIN_CONFIGS.allExtensions.extensions;
              } else if (FREE_DOMAIN_CONFIGS.premiumExtensions.pids.includes(hostingPid)) {
                allowedExtensions = FREE_DOMAIN_CONFIGS.premiumExtensions.extensions;
              }

              const domainExtension = getOnlyTLD(action.payload.domain);
              if (allowedExtensions.includes(domainExtension)) {
                shouldBeFree = true;
              }
            }
          }

          // Set harga domain berdasarkan apakah gratis atau tidak
          const finalPayload = {
            ...action.payload,
            price: shouldBeFree ? 0 : action.payload.price
          };

          state.productsList.push(finalPayload);
          
          // Recalculate cart totals using helper function
          recalculateCartTotals(state);
        } else if (action.payload.type === "backorder-register") {
          if (!checkDomainBackorder) {
            state.productsList.push(action.payload);
            // Recalculate cart totals using helper function
            recalculateCartTotals(state);
          }
        } else if (action.payload.type === "backorder") {
          state.productsList.push(action.payload);
          // Recalculate cart totals using helper function
          recalculateCartTotals(state);
        }
      }
    },
    addDomainBundleToCart: (state, action) => {
      const { onlineId } = action.payload;
      state.domainBundle = {
        onlineId,
      };
    },
    changeDomainFromCart: (state, action) => {
      const index = state.productsList.findIndex(
        (item) => item.domain === action.payload.domain
      );

      if (index !== -1) {
          state.productsList[index] = { ...action.payload };
      } else {
        state.productsList.push(action.payload);
      }

      // Recalculate cart totals using helper function
      recalculateCartTotals(state);
    },
    addDomainTransferToCart: (state, action) => {
      const checkDomain = state.productsList.find(
        (list) => list.domain === action.payload.domain
      );

      if (!checkDomain) {
        // Cek apakah sudah ada hosting dengan durasi minimal 1 tahun di cart
        const hostingItem = state.productsList.find(item => item.type === "hostingaccount");
        const validBillingCycles = ["annually", "biennially", "triennially"];
        let shouldBeFree = false;

        if (hostingItem && validBillingCycles.includes(hostingItem.billingcycle)) {
          // Cek apakah sudah ada domain gratis lain di cart (register ATAU transfer)
          const hasFreeDomainInCart = state.productsList.some(
            item => (item.type === "register" || item.type === "transfer") && 
                    item.price === 0 && 
                    item.duration === "1"
          );

          if (!hasFreeDomainInCart && action.payload.duration === "1") {
            const hostingPid = hostingItem.pid;
            
            // Get allowed extensions based on hosting PID
            let allowedExtensions = [];
            
            if (FREE_DOMAIN_CONFIGS.myidBizidOnly.pids.includes(hostingPid)) {
              allowedExtensions = FREE_DOMAIN_CONFIGS.myidBizidOnly.extensions;
            } else if (FREE_DOMAIN_CONFIGS.comAndSomeId.pids.includes(hostingPid)) {
              allowedExtensions = FREE_DOMAIN_CONFIGS.comAndSomeId.extensions;
            } else if (FREE_DOMAIN_CONFIGS.someIdExtensions.pids.includes(hostingPid)) {
              allowedExtensions = FREE_DOMAIN_CONFIGS.someIdExtensions.extensions;
            } else if (FREE_DOMAIN_CONFIGS.comAndSomeId2.pids.includes(hostingPid)) {
              allowedExtensions = FREE_DOMAIN_CONFIGS.comAndSomeId2.extensions;
            } else if (FREE_DOMAIN_CONFIGS.myidOnly.pids.includes(hostingPid)) {
              allowedExtensions = FREE_DOMAIN_CONFIGS.myidOnly.extensions;
            } else if (FREE_DOMAIN_CONFIGS.allExtensions.pids.includes(hostingPid)) {
              allowedExtensions = FREE_DOMAIN_CONFIGS.allExtensions.extensions;
            } else if (FREE_DOMAIN_CONFIGS.premiumExtensions.pids.includes(hostingPid)) {
              allowedExtensions = FREE_DOMAIN_CONFIGS.premiumExtensions.extensions;
            }

            const domainExtension = getOnlyTLD(action.payload.domain);
            if (allowedExtensions.includes(domainExtension)) {
              shouldBeFree = true;
            }
          }
        }

        // Set harga domain transfer berdasarkan apakah gratis atau tidak
        const finalPayload = {
          ...action.payload,
          price: shouldBeFree ? 0 : action.payload.price
        };

        state.productsList.push(finalPayload);
        // Recalculate cart totals using helper function
        recalculateCartTotals(state);
      }
    },
    addWhoisToCart: (state, action) => {
      // Check if whois already exists for this specific domain
      const checkWhois = state.productsList.find(
        (list) => list.whois === action.payload.whois && 
                  list.domainWhois === action.payload.domainWhois
      );

      // Update isWhois flag for the corresponding domain
      state.productsList = state.productsList.map((obj) => {
        if (
          obj.domain === action.payload.domainWhois &&
          action.payload.freeWhois
        ) {
          obj.isWhois = false;
        } else if (
          obj.domain === action.payload.domainWhois &&
          !action.payload.freeWhois
        ) {
          obj.isWhois = true;
        }
        return obj;
      });

      // Only add whois if it doesn't exist for this domain
      if (!checkWhois) {
        // Set price to 0 if freeWhois is true, otherwise use the provided price
        const finalPayload = {
          ...action.payload,
          price: action.payload.freeWhois ? 0 : (action.payload.priceWithAddon || action.payload.price)
        };

        state.productsList.push(finalPayload);
        
        // Always recalculate cart totals using helper function for consistent PPN calculation
        recalculateCartTotals(state);
      }
    },
    addDomainForwarderToCart: (state, action) => {
      const {
        domain,
        domainForwarder = null,
        activateLater = false,
        addActivate = false,
      } = action.payload;
      const indexProduct = state.productsList.findIndex(
        (list) => list.domain === domain
      );

      if (indexProduct > -1) {
        if (addActivate) {
          state.productsList[indexProduct] = {
            ...state.productsList[indexProduct],
            activateLater,
          };
        } else {
          state.productsList[indexProduct] = {
            ...state.productsList[indexProduct],
            domainForwarder,
          };
        }
      }
    },
    addPaymentMethod: (state, action) => {
      state.paymentMethod = action.payload;
    },
    removePaymentMethod: (state) => {
      state.paymentMethod = "";
      state.additionalFee = 0;
    },
    checkoutProcess: (state, action) => {
      state.orderProcess = action.payload;
    },
    setRequirement: (state, action) => {
      const payloadItem = Object.keys(action.payload);
      if (payloadItem[0] === "company") {
        state.requirement.company = action.payload.company;
      }
      if (payloadItem[0] === "tos") {
        state.requirement.tos = action.payload.tos;
      }
      if (payloadItem[0] === "pph") {
        state.requirement.pph = action.payload.pph;
        if (action.payload.pph) {
          const pricePph = state.subtotal * 0.02;
          state.pricepph = pricePph;
          state.subtotal = state.subtotal - pricePph;
          state.tax.price = state.subtotal * state.tax.percentage;
          state.total = state.subtotal + state.tax.price;
        } else {
          state.subtotal = state.subtotal + state.pricepph;
          state.tax.price = state.subtotal * state.tax.percentage;
          state.total = state.subtotal + state.tax.price;
        }
      }
    },
    setPromoCode: (state, action) => {
      let promoDiscount;
      const { productsList, promo, data } = action.payload;

      // Jika kode promo sudah ada, tidak melakukan apa-apa
      if (state.promoCode.code === promo) return;

      state.promoCode.code = promo;

      // Jika promo adalah 'HOSTINGFREEMYID'
      if (promo === "HOSTINGFREEMYID") {
        promoDiscount = 100 / 100;
        const totalDiscountPrice = state.subtotal * promoDiscount;

        state.promoCode.discountPrice = Math.floor(totalDiscountPrice);
        state.promoCode.description = data.description;
        state.subtotal -= Math.floor(totalDiscountPrice);
        state.tax.price = state.subtotal * state.tax.percentage;
        state.total = state.subtotal + state.tax.price;
      }

      // Untuk promo tipe Percentage
      if (data.type === "Percentage") {
        promoDiscount = +data.price / 100;
        const applicableItems = data.appliesto
          .split(",")
          .map((item) => item.trim());
      
        // console.log("productsList", productsList);
        // console.log("applicableItems", applicableItems);
      
        // Cek apakah ada item berupa angka (product ID)
        const productIndex = state.productsList.findIndex((product) => 
          product?.pid !== undefined && applicableItems.includes(product.pid.toString())
        );
      
        if (productIndex !== -1) {
          // console.log("productIndex", productIndex);
          let totalDiscountPrice = state.productsList[productIndex].price * promoDiscount;
      
          state.promoCode.discountPrice = Math.floor(totalDiscountPrice);
          state.promoCode.description = data.description;
          state.subtotal -= Math.floor(totalDiscountPrice);
          state.tax.price = state.subtotal * state.tax.percentage;
          state.total = state.subtotal + state.tax.price;
        } else {
          // Jika bukan angka, cek domain yang cocok dengan applicableItems
          const productDomain = productsList.find((product) => {
            if (!product?.domain) return false; // Pastikan domain tidak undefined
      
            const domainParts = product.domain.split(".");
            const topLevelDomain =
              domainParts.length === 2
                ? domainParts[1]
                : domainParts.slice(-2).join(".");
      
            return applicableItems.some((item) => {
              const itemParts = item.split(".");
              const itemTLD =
                itemParts.length === 2
                  ? itemParts[1]
                  : itemParts.slice(-2).join(".");
              return topLevelDomain === itemTLD;
            });
          });
          
          if (promo === "SEDEKAHID" || promo === "DOMAINBERKAH") {
            state.promoCode.priceOverrideDomain = 0;
            state.subtotal -= Math.floor(productDomain.price);
            state.subtotal += Math.floor(0);
            state.tax.price = state.subtotal * state.tax.percentage;
            state.total = state.subtotal + state.tax.price;

            const productDomainIndex = productsList.findIndex(
              (p) => p.domain === productDomain.domain
            );
            state.productsList[productDomainIndex].price = 0;

          } else if (productDomain) {
            state.promoCode.priceOverrideDomain = promoDiscount;
            state.subtotal -= Math.floor(productDomain.price);
            state.subtotal += Math.floor(promoDiscount);
            state.tax.price = state.subtotal * state.tax.percentage;
            state.total = state.subtotal + state.tax.price;
      
            const productDomainIndex = productsList.findIndex(
              (p) => p.domain === productDomain.domain
            );
            state.productsList[productDomainIndex].price = promoDiscount;
          }
        }
      }
      // Untuk promo tipe Price Override
      else if (data.type === "Price Override") {
        promoDiscount = parseInt(data.price);
        state.promoCode.discountPrice = Math.floor(promoDiscount);
        state.promoCode.description = data.description;
        const applicableItems = data.appliesto
          .split(",")
          .map((item) => item.trim());

        applicableItems.forEach((item) => {
          if (!isNaN(item)) {
            const product = state.productsList.find(
              (product) => product.pid === parseInt(item)
            );
            const productIndex = state.productsList.findIndex(
              (product) => product.pid === parseInt(item)
            );

            if (product) {
              state.promoCode.priceOverrideHosting = promoDiscount;

              if (promoDiscount < 0) {
                state.subtotal -= Math.floor(product.price);
                state.subtotal += Math.floor(promoDiscount);
              } else {
                state.subtotal -= Math.floor(product.price);
                state.subtotal -= Math.floor(promoDiscount);
              }
              state.tax.price = state.subtotal * state.tax.percentage;
              state.total = state.subtotal + state.tax.price;
              state.productsList[productIndex].price = promoDiscount;
            }
          } else {
            let topLevelDomain;
            const domainParts = item.split(".");
            if (domainParts.length === 2) {
              topLevelDomain = domainParts[domainParts.length - 1];
            } else {
              topLevelDomain =
                domainParts[domainParts.length - 2] +
                "." +
                domainParts[domainParts.length - 2];
            }
            const product = state.productsList.find(
              (product) => product.domain.split(".")[1] === topLevelDomain
            );
            const productIndex = state.productsList.findIndex(
              (product) => product.domain.split(".")[1] === topLevelDomain
            );

            if (product) {
              state.promoCode.priceOverrideDomain = promoDiscount;

              if (promoDiscount < 0) {
                state.subtotal -= Math.floor(product.price);
                state.subtotal += Math.floor(promoDiscount);
              } else {
                state.subtotal -= Math.floor(product.price);
                state.subtotal += Math.floor(promoDiscount);
              }
              state.tax.price = state.subtotal * state.tax.percentage;
              state.total = state.subtotal + state.tax.price;
              state.productsList[productIndex].price = promoDiscount;
            }
          }
        });
      }
      // Untuk promo tipe Fixed Amount
      else if (data.type === "Fixed Amount") {
        promoDiscount = +data.price;
        const totalDiscountPrice = +data.price;

        state.promoCode.discountPrice = Math.floor(totalDiscountPrice);
        state.promoCode.description = data.description;
        state.subtotal -= Math.floor(totalDiscountPrice);
        state.tax.price = state.subtotal * state.tax.percentage;
        state.total = state.subtotal + state.tax.price;
      }
    },
    removePromoCode: (state) => {
      if (
        state.promoCode.realprice &&
        state.promoCode.pid &&
        state.promoCode.queryCode
      ) {
        const productIndex = state.productsList.findIndex(
          (product) => product.pid === parseInt(state.promoCode.pid)
        );
        state.productsList[productIndex].price = state.promoCode.realprice;
      }
      state.subtotal += state.promoCode.discountPrice;
      state.tax.price = state.subtotal * state.tax.percentage;
      state.total = state.subtotal + state.tax.price;
      state.promoCode.code = null;
      state.promoCode.queryCode = null;
      state.promoCode.discountPrice = 0;
    },
    removeDomainBundle: (state) => {
      state.domainBundle = {
        onlineId: null,
      };
    },
    removeDomainForwarderFromCart: (state, action) => {
      const { domain } = action.payload;
      const indexProduct = state.productsList.findIndex(
        (list) => list.domain === domain && list.type === "register"
      );

      if (indexProduct > -1) {
        state.productsList[indexProduct].domainForwarder = undefined;
      }
    },
    setOrderProcessName: (state, action) => {
      state.orderProcessName = action.payload;
    },
    addVpsAddons: (state, action) => {
      const { pid, addons } = action.payload;
      const { input_name } = addons;
      
      const findItem = state.productsList.find((item) => item.pid === pid);
      
      if (!findItem) {
        console.warn(`Product with pid ${pid} not found in productsList.`);
        return;
      }
      
      const indexAddons = findItem?.vpsaddons?.findIndex(
        (item) => item.input_name === input_name
      );

      // Prepare addon data with proper structure
      const processedAddon = {
        ...addons,
        // Ensure price and setupfee are numbers
        price: Number(addons.price || 0),
        setupfee: Number(addons.setupfee || 0),
        // Add billing cycle specific pricing if available
        ...(addons.billingcycle && {
          [addons.billingcycle]: addons[addons.billingcycle] || '0.00',
          [addons.billingcycle[0] + 'setupfee']: addons[addons.billingcycle[0] + 'setupfee'] || '0.00'
        })
      };

      if (indexAddons === -1) {
        // Add new addon
        if (!findItem.vpsaddons) {
          findItem.vpsaddons = [];
        }
        findItem.vpsaddons.push(processedAddon);
      } else {
        // Replace existing addon
        findItem.vpsaddons.splice(indexAddons, 1, processedAddon);
      }
      
      // Update priceWithAddon to reflect the new total
      const newTotalPrice = calculateProductTotalPrice(findItem);
      findItem.priceWithAddon = newTotalPrice;

      console.log('🛒 Updated product with addon:', {
        productName: findItem.name,
        addonName: processedAddon.name,
        addonPrice: processedAddon.price,
        addonSetupFee: processedAddon.setupfee,
        newTotalPrice,
        totalAddons: findItem.vpsaddons.length
      });

      // Recalculate cart totals using helper function
      recalculateCartTotals(state);
    },
    removeVpsAddons: (state, action) => {
      const { pid, input } = action.payload;
      const updatedList = state.productsList.map((product) => {
        if (product.pid === pid) {
          return {
            ...product,
            vpsaddons: product.vpsaddons.filter(
              (addon) => addon.input_name !== input
            ),
          };
        }
        return product;
      });
      state.productsList = updatedList;

      // Find the item in the updated list
      const findItem = state.productsList.find((item) => item.pid === pid);

      // Check if findItem is defined
      if (!findItem) {
        console.warn(`Product with pid ${pid} not found in productsList.`);
        return; // Exit the function if findItem is undefined
      }

      // Recalculate cart totals using helper function
      recalculateCartTotals(state);
    },
    addConfigOption: (state, action) => {
      const { pid, addons } = action.payload;
      const { input_name } = addons;
      const findItem = state.productsList.find((item) => item.pid === pid);
      
      if (!findItem) {
        console.warn(`Product with pid ${pid} not found in productsList.`);
        return;
      }
      
      const indexAddons = findItem.configoptions.findIndex(
        (item) => item.input_name === input_name
      );

      if (indexAddons === -1) {
        // Add new config option
        if (!findItem.configoptions) {
          findItem.configoptions = [];
        }
        findItem.configoptions.push(addons);
      } else {
        // Replace existing config option
        findItem.configoptions.splice(indexAddons, 1, addons);
      }

      // Recalculate cart totals using helper function
      recalculateCartTotals(state);
    },
    removeConfigOption: (state, action) => {
      const { pid, input } = action.payload;
      const updatedList = state.productsList.map((product) => {
        if (product.pid === pid) {
          return {
            ...product,
            configoptions: product.configoptions.filter(
              (addon) => addon.input_name !== input
            ),
          };
        }
        return product;
      });
      state.productsList = updatedList;
      
      // Recalculate cart totals using helper function
      recalculateCartTotals(state);
    },
    addCustomfieldVps: (state, action) => {
      const { pid, id, value } = action.payload;
      const findItem = state.productsList.find((item) => item.pid === pid);
      
      if (!findItem) {
        console.warn(`Product with pid ${pid} not found in productsList.`);
        return;
      }
      
      const indexCustomfield = findItem.customfields.findIndex(
        (item) => item.id === id
      );
      const customfield = { id: id, value: value };
      
      if (indexCustomfield === -1) {
        findItem.customfields.push(customfield);
      } else {
        findItem.customfields.splice(indexCustomfield, 1, customfield);
      }
      
      // Recalculate cart totals using helper function
      recalculateCartTotals(state);
    },
    removeCustomfieldVps: (state, action) => {
      const { pid, id } = action.payload;
      const updatedList = state.productsList.map((product) => {
        if (product.pid === pid) {
          return {
            ...product,
            customfields: product.customfields.filter(
              (field) => field.id !== id
            ),
          };
        }
        return product;
      });
      state.productsList = updatedList;
      
      // Recalculate cart totals using helper function
      recalculateCartTotals(state);
    },
    addDomainCondition: (state, action) => {
      state.conditionalDomain = action.payload;
    },
    addDocumentNpwp: (state, action) => {
      state.documentNpwp = action.payload;
    },
    updateStatusSubdomain: (state, action) => {
      state.documentSuratSubdomain.status = action.payload;
    },
    deletedWhois: (state, action) => {
      // Ambil domain dari payload (bisa berupa 'namadomain.com,30000')
      const domain = action.payload.split(",")[0];

      // Set isWhois pada domain utama menjadi false
      state.productsList = state.productsList.map((item) => {
        if (item.domain === domain) {
          return { ...item, isWhois: false };
        }
        return item;
      });

      // Hapus item whois yang terkait dengan domain tersebut
      state.productsList = state.productsList.filter(
        (item) => !(item.type === "whois" && item.domainWhois === domain)
      );
    },
    resetUploadStatus: (state, action) => {
      state.ktpCard.uploadStatus = action.payload;
    },
    resetStatusNPWP: (state, action) => {
      state.documentNpwp.status = action.payload;
    },
    resetStatusPph: (state, action) => {
      state.documentPph.status = action.payload;
    },
    updateUrlPph: (state, action) => {
      state.documentPph.url = action.payload;
    },
    addConvenienceFee: (state, action) => {
      if (action.payload.feetype === "percent") {
        state.subtotal -= state.additionalFee;
        const percent = state.subtotal * parseFloat(action.payload.price);
        state.subtotal += percent;
        state.tax.price = state.subtotal * state.tax.percentage;
        state.total = state.subtotal + state.tax.price;
        state.additionalFee = percent;
      } else if (action.payload.feetype === "percent and fee") {
        state.subtotal -= state.additionalFee;
        const percent = state.subtotal * action.payload.price;
        const added = parseInt(percent) + parseInt(action.payload.add);
        state.subtotal += added;
        state.tax.price = state.subtotal * state.tax.percentage;
        state.total = state.subtotal + state.tax.price;
        state.additionalFee = added;
      } else {
        state.subtotal -= state.additionalFee;
        state.subtotal += parseInt(action.payload.price);
        state.tax.price = state.subtotal * state.tax.percentage;
        state.total = state.subtotal + state.tax.price;
        state.additionalFee = action.payload.price;
      }
    },
    setQueryPromo: (state, action) => {
      state.promoCode.queryCode = action.payload;
    },
    addPremiumToCart: (state, action) => {
      const checkPremiumDomain = state.productsList.find(
        (list) => list.premium_domain === action.payload.premium_domain
      );
      if (!checkPremiumDomain) {
        state.productsList.push(action.payload);
        state.subtotal += +action.payload.price;
        state.tax.price = state.subtotal * state.tax.percentage;
        state.total = state.subtotal + state.tax.price;
      }
    },
    skipAllConditionalDomain: (state, action) => {
      state.conditionalDomain.isSkipAll = action.payload;
    },
    addPromo: (state, action) => {
      let promoDiscount;

      state.promoCode.code = action.payload.promocode;
      state.promoCode.realprice = action.payload.realprice;
      state.promoCode.pid = action.payload.pid;

      if (action.payload.type === "Percentage") {
        promoDiscount =
          action.payload.type === "Percentage"
            ? +action.payload.price / 100
            : +action.payload.price;

        const totalDiscountPrice =
          action.payload.type === "Percentage"
            ? action.payload.realprice * promoDiscount
            : +action.payload.price;

        state.promoCode.discountPrice = Math.floor(totalDiscountPrice);
        state.promoCode.description = action.payload.description;
      } else if (action.payload.type === "Price Override") {
        promoDiscount = parseInt(action.payload.price);
        state.promoCode.discountPrice = Math.floor(promoDiscount);
        state.promoCode.description = action.payload.description;
        const applicableItems = action.payload.appliesto
          .split(",")
          .map((item) => item.trim());
        applicableItems.forEach((item) => {
          if (!isNaN(item)) {
            const product = state.productsList.find(
              (product) => product.pid === parseInt(item)
            );
            const productIndex = state.productsList.findIndex(
              (product) => product.pid === parseInt(item)
            );

            if (product) {
              state.promoCode.priceOverrideHosting = promoDiscount;

              if (promoDiscount < 0) {
                state.productsList[productIndex].price = promoDiscount;
              } else {
                state.productsList[productIndex].price = promoDiscount;
              }
            }
          } else {
            let topLevelDomain;
            const domainParts = item.split(".");
            if (domainParts.length === 2) {
              topLevelDomain = domainParts[domainParts.length - 1];
            } else {
              topLevelDomain =
                domainParts[domainParts.length - 2] +
                "." +
                domainParts[domainParts.length - 2];
            }
            const product = state.productsList.find(
              (product) => product.domain === parseInt(topLevelDomain)
            );
            const productIndex = state.productsList.findIndex(
              (product) => product.domain === parseInt(topLevelDomain)
            );
            if (product) {
              state.promoCode.priceOverrideDomain = promoDiscount;

              if (promoDiscount < 0) {
                state.productsList[productIndex].price = promoDiscount;
              } else {
                state.productsList[productIndex].price = promoDiscount;
              }
            }
          }
        });
      } else if (action.payload.type === "Fixed Amount") {
        promoDiscount =
          action.payload.type === "Fixed Amount" ? +action.payload.price : 0;

        const totalDiscountPrice =
          action.payload.type === "Fixed Amount" ? +action.payload.price : 0;

        state.promoCode.discountPrice = Math.floor(totalDiscountPrice);
        state.promoCode.description = action.payload.description;
      }
    },
    addPromoDomain: (state, action) => {
      state.promoCode.code = action.payload.promocode;
      state.promoCode.discountPrice = Math.floor(action.payload.price);
    },
    addPromoKaos: (state, action) => {
      state.promoCode.customPromo = action.payload.promoKaos
        ? "Promo Kaos Tumbler"
        : "";
    },
    addPaymentGateway: (state, action) => {
      state.promoCode.paymentGateway = action.payload.paymentGateway
        ? "Gratis Payment Gateway"
        : "";
    },
    removePpn: (state) => {
      state.total = state.total - state.tax.price;
      state.tax.price = 0;
    },
    clearCart: (action) => {
      return {
        ...initialState,
        orderProcessName: action.payload,
      };
    },
    setOrderParams: (state, action) => {
      state.orderParams = action.payload;
    },
    setVelocityProduct: (state, action) => {
      state.velocityProduct = action.payload;
    },
    clearVelocityProduct: (state) => {
      state.velocityProduct = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendDocumentNpwp.pending, (state) => {
        state.documentNpwp.status = "loading";
      })
      .addCase(sendDocumentNpwp.fulfilled, (state, action) => {
        if (action.payload.error) {
          state.documentNpwp.status = "error";
          state.documentNpwp.error = action.payload.message;
        } else {
          state.documentNpwp.status = "fulfilled";
          state.npwpUrl = action.payload.data.url;
          state.documentNpwp.error = "";
        }
      })
      .addCase(sendDocumentNpwp.rejected, (state, action) => {
        state.documentNpwp.status = "error";
        state.documentNpwp.error = action.error.message;
      })
      .addCase(sendDocumentPph.pending, (state) => {
        state.documentPph.status = "loading";
      })
      .addCase(sendDocumentPph.fulfilled, (state, action) => {
        if (action.payload.error) {
          state.documentPph.status = "error";
          state.documentPph.error = action.payload.message;
        } else {
          state.documentPph.status = "fulfilled";
          state.documentPph.url = action.payload.data.url;
          state.documentPph.error = "";
        }
      })
      .addCase(sendDocumentPph.rejected, (state, action) => {
        state.documentPph.status = "error";
        state.documentPph.error = action.error.message;
      })
      .addCase(sendDocumentSubdomain.pending, (state) => {
        state.documentSuratSubdomain.status = "loading";
      })
      .addCase(sendDocumentSubdomain.fulfilled, (state, action) => {
        if (action.payload.error) {
          state.documentSuratSubdomain.status = "error";
          state.documentSuratSubdomain.error = action.payload.message;
        } else {
          state.documentSuratSubdomain.status = "fulfilled";
          state.documentSuratSubdomain.suratUrl = action.payload.data.url;
          state.documentSuratSubdomain.error = "";
        }
      })
      .addCase(sendDocumentSubdomain.rejected, (state, action) => {
        state.documentSuratSubdomain.status = "error";
        state.documentSuratSubdomain.error = action.error.message;
      })
      .addCase(sendDocumentDomainTerm.pending, (state) => {
        state.conditionalDomain.status = "loading";
      })
      .addCase(sendDocumentDomainTerm.fulfilled, (state, action) => {
        if (action.payload.error) {
          state.conditionalDomain.status = "error";
          state.conditionalDomain.error = action.payload.message;
        } else {
          state.conditionalDomain.status = "fulfilled";
          state.conditionalDomain.error = "";
        }
      })
      .addCase(sendDocumentDomainTerm.rejected, (state, action) => {
        state.conditionalDomain.status = "error";
        state.conditionalDomain.error = action.error.message;
      })
      .addCase(sendCheckOnlineId.pending, (state) => {
        state.availableOnlineId = {
          status: "loading",
          data: null,
        };
      })
      .addCase(sendCheckOnlineId.fulfilled, (state, action) => {
        if (action.payload.error) {
          state.availableOnlineId = {
            status: "error",
            error: action.payload.message,
            data: null,
          };
        } else {
          state.availableOnlineId = {
            status: "fulfilled",
            error: "",
            data: action.payload.data,
          };
        }
      })
      .addCase(sendCheckOnlineId.rejected, (state, action) => {
        state.availableOnlineId = {
          status: "error",
          error: action.error.message,
          data: null,
        };
      })
      .addCase(uploadKTP.pending, (state) => {
        state.ktpCard.uploadStatus = "uploading";
        state.ktpCard.message = "Please Wait";
      })
      .addCase(uploadKTP.fulfilled, (state, action) => {
        const { url, code, msg } = action.payload?.data?.qw_upload;
        state.ktpCard.imgUrlResponse = url;
        state.ktpCard.code = code;
        state.ktpCard.message = msg;
        state.ktpCard.uploadStatus = "user_agree";
      })
      .addCase(uploadKTP.rejected, (state, action) => {
        state.ktpCard.imgUrlResponse = "";
        state.ktpCard.code = 400;
        state.ktpCard.message = action.payload;
        state.ktpCard.uploadStatus = "fail_upload";
      })
      .addCase(getMyVoucher.pending, (state) => {
        state.promoCode.status = "loading";
      })
      .addCase(getMyVoucher.fulfilled, (state, action) => {
        state.promoCode.status = "fulfilled";
        
        // Handle response dari PHP API yang baru
        if (action.payload.code === 200 && action.payload.promoList) {
          // Success response dengan data promo
          state.promoCode.myVoucher = Array.isArray(action.payload.promoList) ? action.payload.promoList : [];
        } else if (action.payload.code === 401 || action.payload.message === "Current user not authenticated as User.") {
          // Not authenticated
          state.promoCode.myVoucher = "Anda Belum Login";
        } else if (action.payload.code === 400 || action.payload.message === "No user session found") {
          // No session
          state.promoCode.myVoucher = "Anda Belum Login";
        } else if (typeof action.payload === 'string' && action.payload === "Current user not authenticated as User.") {
          // Legacy string response
          state.promoCode.myVoucher = "Anda Belum Login";
        } else {
          // Fallback untuk format lama atau error
          state.promoCode.myVoucher = action.payload.data?.promoList || action.payload.promoList || null;
        }
      })
      .addCase(getMyVoucher.rejected, (state, action) => {
        state.promoCode.status = "error";
        state.error = action.error.message;
      })
      .addCase(sendCheckOnlineIdExist.pending, (state) => {
        state.onlineIdCheckExist = {
          status: "loading",
          error: "",
          exist: false,
        };
      })
      .addCase(sendCheckOnlineIdExist.fulfilled, (state, action) => {
        const { exist, error = null } = action.payload.data;
        if (error) {
          state.onlineIdCheckExist = {
            status: "error",
            error: action.payload.message,
            exist: false,
          };
        } else {
          state.onlineIdCheckExist = {
            status: "fulfilled",
            error: "",
            exist,
          };
        }
      })
      .addCase(sendCheckOnlineIdExist.rejected, (state, action) => {
        state.onlineIdCheckExist = {
          status: "error",
          exist: false,
          error: action.error.message,
        };
      });
  },
});

export default cartSlice.reducer;
export const {
  addToCart,
  addDomainToCart,
  addDomainBundleToCart,
  addDomainForwarderToCart,
  addWhoisToCart,
  removeCartItem,
  checkoutProcess,
  addPaymentMethod,
  setRequirement,
  setPromoCode,
  removePromoCode,
  removeDomainBundle,
  removeDomainForwarderFromCart,
  setOrderProcessName,
  addDomainCondition,
  addVpsAddons,
  removeVpsAddons,
  removeConfigOption,
  addConfigOption,
  addDomainTransferToCart,
  addDocumentNpwp,
  deletedWhois,
  clearCart,
  resetStatusNPWP,
  resetStatusPph,
  resetUploadStatus,
  addConvenienceFee,
  removePaymentMethod,
  setQueryPromo,
  addPremiumToCart,
  changeDomainFromCart,
  changeProductFromCart,
  addCustomfieldVps,
  removeCustomfieldVps,
  skipAllConditionalDomain,
  addPromo,
  addPromoKaos,
  addPaymentGateway,
  removePpn,
  updateStatusSubdomain,
  updateUrlPph,
  addPromoDomain,
  setOrderParams,
  setVelocityProduct,
  clearVelocityProduct,
} = cartSlice.actions;
